import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  CanLoad,
  Route,
  Router,
  UrlTree,
} from '@angular/router';
import { ProjectsService } from '@front-end/core/services';
import { CostCenterSummary, RoleEnum } from '@libs/core/api';
import { catchError, map, Observable, of } from 'rxjs';
import { ToastService } from '../services';
import { NgxAuthService } from 'ngx-auth-utils';

interface AuthorizationGuardData {
  roles: RoleEnum[];
  redirectTo: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthorizationGuard
  implements CanActivate, CanActivateChild, CanLoad
{
  private project$ = this.projectsService.selectedProject$;

  constructor(
    private router: Router,
    private authService: NgxAuthService,
    private projectsService: ProjectsService,
    private toastService: ToastService
  ) {}

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    return this.can(route);
  }

  canActivateChild(
    route: ActivatedRouteSnapshot
  ): Observable<boolean | UrlTree> {
    return this.can(route);
  }

  canLoad(route: Route): Observable<boolean | UrlTree> {
    return this.can(route);
  }

  private can(
    route: ActivatedRouteSnapshot | Route
  ): Observable<boolean | UrlTree> {
    const data = this.getData(route);

    if (!this.authService.snapshot.authenticated) {
      return of(this.router.parseUrl('/'));
    }

    return this.project$.pipe(
      map((project) => {
        const isAuthorized = this.isAuthorized(project, data.roles);

        return isAuthorized ? true : this.router.parseUrl(data.redirectTo);
      }),
      catchError(() => {
        this.toastService.warn('messages.errors.projectNotFound');
        return of(this.router.parseUrl('/user/cost-centers'));
      })
    );
  }

  private isAuthorized(project: CostCenterSummary, roles: RoleEnum[]): boolean {
    return roles && roles.includes(project.role as RoleEnum);
  }

  private getData(
    route: ActivatedRouteSnapshot | Route
  ): AuthorizationGuardData {
    return route.data as AuthorizationGuardData;
  }
}
